import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { Card, Button, ProgressBar, Alert, Badge, Spinner, Modal } from 'react-bootstrap';
import {
  Quiz,
  Question,
  quizService,
  QuizAttemptResponse,
  UnlockedAchievementPayload,
  UnlockedCharacterPayload,
  LoginStreakHome,
  authService,
} from '../services/api';
import QuizFinishCelebrationModal from './QuizFinishCelebrationModal';
import LevelUpCelebrationModal from './LevelUpCelebrationModal';
import { useQuizFinishLevelUp } from '../hooks/useQuizFinishLevelUp';
import { useQuizTimer } from '../hooks/useQuizTimer';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import { isPwaStandalone } from '../utils/pwaStandalone';
import WitcQuizPlayer from './WitcQuizPlayer';
import SoloQuizResultsModal from './SoloQuizResultsModal';
import { getQuizFullDisplayCaption, pickBilingualLine } from '../utils/quizDisplayTitle';
import QuizPlayPageHeading from './QuizPlayPageHeading';
import { quizQuestionImageUrlsFromKey } from '../utils/quizQuestionImageSrc';
import McImageAnswerButton from './McImageAnswerButton';
import { quizQuestionPanelBackground } from '../utils/quizQuestionPanelBackground';
import {
  maxPointsForPlayDifficultyBand,
  normalizePlayDifficultyLabel,
  witcTimeLimitSecondsForBand,
} from '../utils/quizPlayBandScoring';
import { playQuizSfxCorrect, playQuizSfxIncorrect } from '../audio/quizSfx';
import { useMcWasm } from '../hooks/useMcWasm';
import { onQuizSkipClick, useBlurMobileSkipFocusOnQuestionChange } from '../utils/quizSkipButton';
import { WITC_REVEAL_DELAY_MS } from '../utils/witcExpectedAnswer';
import { isQuickMatchWitcQuestion } from '../utils/quickMatchQuestion';
import QuizSoundToggleButton from './QuizSoundToggleButton';
import NavbarMusicToggle from './NavbarMusicToggle';
import './shared/QuizStyles.css';
import './QuizPlayer.css';

const TIME_LIMIT_MC = 30;

type QuickMatchMixedPlayerProps = {
  quizId: string;
  quiz: Quiz;
  questions: Question[];
  startTime: Date;
  witcPlaySessionId: string | null;
  playDifficultiesPerQuestion: string[] | null;
  soloPlayDifficulty: string | null;
  onPlayAgain: () => void | Promise<void>;
  playAgainLoading: boolean;
  /** Parent shows inventory reminder — hide results modal underneath */
  suppressResultsModal?: boolean;
};

const QuickMatchMixedPlayer: React.FC<QuickMatchMixedPlayerProps> = ({
  quizId,
  quiz,
  questions,
  startTime,
  witcPlaySessionId,
  playDifficultiesPerQuestion,
  soloPlayDifficulty,
  onPlayAgain,
  playAgainLoading,
  suppressResultsModal = false,
}) => {
  const { t, i18n } = useSafeTranslation();
  const navigate = useNavigate();

  const [slotIndex, setSlotIndex] = useState(0);
  const [runningScore, setRunningScore] = useState(0);
  const [runningCorrect, setRunningCorrect] = useState(0);

  const answersAccRef = useRef<string[]>([]);
  const witcAccRef = useRef<Array<{ pointsEarned: number; finalAnswerText?: string | null }>>([]);
  const secondsAccRef = useRef<number[]>([]);

  const [savingQuizAttempt, setSavingQuizAttempt] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [resultDurationSeconds, setResultDurationSeconds] = useState<number | null>(null);
  const [earnedXP, setEarnedXP] = useState(0);
  const [quizResponse, setQuizResponse] = useState<QuizAttemptResponse | null>(null);
  const [isQuizCompleted, setIsQuizCompleted] = useState(false);
  const [hasTooManySkips, setHasTooManySkips] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationAchievements, setCelebrationAchievements] = useState<UnlockedAchievementPayload[]>([]);
  const [celebrationCharacters, setCelebrationCharacters] = useState<UnlockedCharacterPayload[]>([]);
  const [celebrationLoginStreak, setCelebrationLoginStreak] = useState<LoginStreakHome | null>(null);
  const [celebrationPlayerNick, setCelebrationPlayerNick] = useState('');
  const levelUpCelebration = useQuizFinishLevelUp();
  const [saveAttemptError, setSaveAttemptError] = useState<string | null>(null);

  const finishSubmittedRef = useRef(false);
  const witcMountKeyRef = useRef(0);

  const n = questions.length;
  const bands = playDifficultiesPerQuestion;

  useEffect(() => {
    answersAccRef.current = new Array(n).fill('skip');
    witcAccRef.current = new Array(n).fill(null).map(() => ({ pointsEarned: 0 }));
    secondsAccRef.current = new Array(n).fill(TIME_LIMIT_MC);
  }, [n]);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setCelebrationPlayerNick('');
      return;
    }
    authService
      .getProfile()
      .then((u) => {
        const nick = (u.nickname && u.nickname.trim()) || u.name || '';
        setCelebrationPlayerNick(nick);
      })
      .catch(() => setCelebrationPlayerNick(''));
  }, []);

  const submitAll = useCallback(async () => {
    if (finishSubmittedRef.current || !bands || bands.length !== n) return;
    finishSubmittedRef.current = true;
    setSavingQuizAttempt(true);
    setIsQuizCompleted(true);
    const finishedAt = new Date();
    const duration = Math.max(0, Math.floor((finishedAt.getTime() - startTime.getTime()) / 1000));
    setResultDurationSeconds(duration);

    const saveQuizId = questions[0]?.quizId || quizId;
    const answersOut = answersAccRef.current.map((a) => a || 'skip');
    const timesOut = secondsAccRef.current.map((s, i) => {
      const limit = witcTimeLimitSecondsForBand(bands[i] || 'Easy');
      return s > 0 && s <= limit ? s : Math.min(s, limit);
    });

    try {
      const response = await quizService.saveQuizAttempt(
        {
          quizId: saveQuizId,
          score: runningScore,
          correctAnswers: runningCorrect,
          totalQuestions: n,
          duration,
          isCompleted: true,
          answers: answersOut,
          questionIdsInOrder: questions.map((q) => q.id),
          responseTimeSecondsPerQuestion: timesOut,
          soloQuickMatch: true,
          playDifficultiesPerQuestion: bands,
          witcResults: witcAccRef.current.map((w) => ({
            pointsEarned: w.pointsEarned,
            finalAnswerText: w.finalAnswerText ?? undefined,
          })),
        },
        { pwaClient: isPwaStandalone() }
      );
      setQuizResponse(response);
      if (response.earnedXP !== undefined) setEarnedXP(response.earnedXP);
      if (response.tooManySkips) {
        setHasTooManySkips(true);
        setIsQuizCompleted(false);
      }
      const unlocked = response.unlockedAchievements?.filter(Boolean) ?? [];
      const unlockedChars = response.unlockedCharacters?.filter(Boolean) ?? [];
      const streakUpdate = response.loginStreakUpdated ?? null;
      const hasLevelUp = levelUpCelebration.prepareFromQuizResponse(response);
      if ((unlocked.length > 0 || unlockedChars.length > 0 || streakUpdate) && !response.tooManySkips) {
        setCelebrationAchievements(unlocked);
        setCelebrationCharacters(unlockedChars);
        setCelebrationLoginStreak(streakUpdate);
        setShowCelebration(true);
      } else if (hasLevelUp && levelUpCelebration.tryOpenLevelUpCelebration()) {
        /* level-up modal */
      } else {
        setShowResultsModal(true);
      }
    } catch (err: unknown) {
      finishSubmittedRef.current = false;
      setIsQuizCompleted(false);
      const msg = axios.isAxiosError(err)
        ? String((err.response?.data as { message?: string })?.message ?? err.message)
        : err instanceof Error
          ? err.message
          : 'Unknown error';
      setSaveAttemptError(`Failed to save quiz results: ${msg}`);
    } finally {
      setSavingQuizAttempt(false);
    }
  }, [bands, n, questions, quiz, quizId, runningCorrect, runningScore, soloPlayDifficulty, startTime]);

  useEffect(() => {
    if (slotIndex === n && n > 0 && !finishSubmittedRef.current) {
      void submitAll();
    }
  }, [slotIndex, n, submitAll]);

  const handleWitcSlot = (payload: {
    mergedAnswers: Map<number, string>;
    witcResult: { pointsEarned: number; finalAnswerText?: string | null };
    responseSeconds: number;
  }) => {
    const tok = payload.mergedAnswers.get(0) ?? 'skip';
    answersAccRef.current[slotIndex] = tok === 'skip' ? 'skip' : 'witc';
    witcAccRef.current[slotIndex] = {
      pointsEarned: payload.witcResult.pointsEarned,
      finalAnswerText: payload.witcResult.finalAnswerText,
    };
    secondsAccRef.current[slotIndex] = payload.responseSeconds;
    setRunningScore((s) => s + payload.witcResult.pointsEarned);
    if (payload.witcResult.pointsEarned > 0) setRunningCorrect((c) => c + 1);
    witcMountKeyRef.current += 1;
    setSlotIndex((i) => i + 1);
  };

  const handleBackToHome = () => {
    setShowResultsModal(false);
    setShowCelebration(false);
    setCelebrationAchievements([]);
    setCelebrationCharacters([]);
    setCelebrationLoginStreak(null);
    levelUpCelebration.clearPendingLevelUp();
    navigate('/', { replace: true });
  };

  const handleAbandonSession = useCallback(() => {
    const quitAt = new Date();
    const secs = Math.max(0, Math.floor((quitAt.getTime() - startTime.getTime()) / 1000));
    setResultDurationSeconds(secs);
    setIsQuizCompleted(false);
    setShowResultsModal(true);
  }, [startTime]);

  const handleCelebrationFinished = () => {
    setShowCelebration(false);
    setCelebrationAchievements([]);
    setCelebrationCharacters([]);
    setCelebrationLoginStreak(null);
    if (levelUpCelebration.tryOpenLevelUpCelebration()) {
      return;
    }
    setShowResultsModal(true);
  };

  const handleLevelUpFinished = () => {
    levelUpCelebration.closeLevelUpCelebration();
    setShowResultsModal(true);
  };

  const sessionFooterModals = (
    <>
      <QuizFinishCelebrationModal
        show={showCelebration}
        achievements={celebrationAchievements}
        characters={celebrationCharacters}
        loginStreak={celebrationLoginStreak}
        onFinished={handleCelebrationFinished}
        playerNickname={celebrationPlayerNick}
      />
      <LevelUpCelebrationModal
        show={levelUpCelebration.showLevelUp}
        oldLevel={levelUpCelebration.levelUpOld}
        newLevel={levelUpCelebration.levelUpNew}
        onClose={handleLevelUpFinished}
      />
      <SoloQuizResultsModal
        show={showResultsModal && !suppressResultsModal}
        hasTooManySkips={hasTooManySkips}
        isQuizCompleted={isQuizCompleted}
        quizTitleDisplay={getQuizFullDisplayCaption(quiz, i18n.language)}
        correctAnswers={runningCorrect}
        totalQuestions={n}
        score={runningScore}
        earnedXP={earnedXP}
        quizResponse={quizResponse}
        resultDurationSeconds={resultDurationSeconds}
        onPlayAgain={onPlayAgain}
        onBackToHome={handleBackToHome}
        playAgainLoading={playAgainLoading}
      />
    </>
  );

  if (saveAttemptError) {
    return (
      <div className="quiz-error">
        <Alert variant="danger">
          <Alert.Heading>{t('common.error', 'Error')}</Alert.Heading>
          <p>{saveAttemptError}</p>
          <Button variant="outline-danger" onClick={handleBackToHome}>
            {t('quiz.backToHome', 'Back to Home')}
          </Button>
        </Alert>
      </div>
    );
  }

  if (slotIndex < n) {
    const q = questions[slotIndex];
    const isWitc = isQuickMatchWitcQuestion(q);

    if (isWitc) {
      const band = bands?.[slotIndex] ?? soloPlayDifficulty ?? quiz.difficulty ?? 'Easy';
      return (
        <>
          <WitcQuizPlayer
            key={`qm-witc-${witcMountKeyRef.current}-${q.id}`}
            quizId={quizId}
            quiz={{ ...quiz, format: 'Witc' }}
            questions={[q]}
            startTime={startTime}
            witcPlaySessionId={witcPlaySessionId}
            playDifficulty={band}
            playDifficultyPerQuestion={bands ? [bands[slotIndex]] : null}
            soloQuickMatch
            mixedQuickMatchSlot
            onMixedQuickMatchSlotComplete={handleWitcSlot}
            mixedSlotHeader={{
              displayScore: runningScore,
              displayCorrect: runningCorrect,
              questionOneBased: slotIndex + 1,
              totalQuestions: n,
            }}
            onPlayAgain={onPlayAgain}
            playAgainLoading={playAgainLoading}
            suppressResultsModal={suppressResultsModal}
          />
          {sessionFooterModals}
        </>
      );
    }

    return (
      <>
      <QuickMatchMcSlot
        key={`qm-mc-${q.id}-${slotIndex}`}
        quiz={quiz}
        quizId={quizId}
        question={q}
        slotIndex={slotIndex}
        totalQuestions={n}
        band={bands?.[slotIndex] ?? soloPlayDifficulty ?? quiz.difficulty ?? 'Easy'}
        runningScore={runningScore}
        runningCorrect={runningCorrect}
        onAbandonSession={handleAbandonSession}
        onComplete={(payload) => {
          answersAccRef.current[slotIndex] = payload.answerId;
          witcAccRef.current[slotIndex] = { pointsEarned: 0 };
          secondsAccRef.current[slotIndex] = payload.seconds;
          setRunningScore((s) => s + payload.scoreDelta);
          setRunningCorrect((c) => c + payload.correctDelta);
          setSlotIndex((i) => i + 1);
        }}
      />
      {sessionFooterModals}
      </>
    );
  }

  return (
    <div className="quiz-page">
      <div className="quiz-container" />

      {savingQuizAttempt && (
        <div className="quiz-finish-saving-overlay" role="status" aria-live="polite" aria-busy="true">
          <div className="quiz-finish-saving-overlay__inner">
            <Spinner animation="border" role="presentation" />
            <span className="text-white mt-2 small">
              {t('quiz.savingResults', 'Saving results...')}
            </span>
          </div>
        </div>
      )}

      {sessionFooterModals}
    </div>
  );
};

type McSlotProps = {
  quiz: Quiz;
  quizId: string;
  question: Question;
  slotIndex: number;
  totalQuestions: number;
  band: string;
  runningScore: number;
  runningCorrect: number;
  onAbandonSession: () => void;
  onComplete: (payload: {
    answerId: string;
    seconds: number;
    scoreDelta: number;
    correctDelta: number;
  }) => void;
};

const QuickMatchMcSlot: React.FC<McSlotProps> = ({
  quiz,
  quizId,
  question,
  slotIndex,
  totalQuestions,
  band,
  runningScore,
  runningCorrect,
  onAbandonSession,
  onComplete,
}) => {
  const { t, i18n } = useSafeTranslation();
  const [showQuitModal, setShowQuitModal] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [mcValidateResult, setMcValidateResult] = useState<{
    correct: boolean;
    correctAnswerId?: string;
  } | null>(null);
  const [mcValidating, setMcValidating] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);

  const bandLabel = useMemo(() => normalizePlayDifficultyLabel(band), [band]);
  const mcLimit = useMemo(() => witcTimeLimitSecondsForBand(band), [band]);
  const questionShownAtRef = useRef(Date.now());
  const secondsRef = useRef(0);

  const [slotCipher, setSlotCipher] = useState<string | null>(null);
  const [slotLoadError, setSlotLoadError] = useState<string | null>(null);

  const {
    ready: wasmReady,
    error: wasmError,
    submitAnswer: wasmSubmitAnswer,
  } = useMcWasm(slotCipher ?? undefined);

  useEffect(() => {
    let cancelled = false;
    setSlotCipher(null);
    setSlotLoadError(null);
    (async () => {
      try {
        const start = await quizService.startQuickMatchMcSlot(question.id, band);
        if (cancelled) return;
        if (!start.engineBootstrapCipher?.trim()) {
          setSlotLoadError('Multiple Choice engine failed to load.');
          return;
        }
        setSlotCipher(start.engineBootstrapCipher);
      } catch (e) {
        if (cancelled) return;
        setSlotLoadError(
          e instanceof Error ? e.message : 'Multiple Choice engine failed to load.'
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [question.id, band]);

  const advanceSlotSkipRef = useRef<(seconds: number) => void>(() => {});

  const { timeLeft, resetTimer, stopTimer } = useQuizTimer({
    timeLimit: mcLimit,
    onTimeout: () => {
      if (selectedAnswer !== null) return;
      advanceSlotSkipRef.current(mcLimit);
    },
    isActive: selectedAnswer === null,
  });

  const advanceSlotSkip = (seconds: number) => {
    secondsRef.current = seconds;
    stopTimer();
    onComplete({
      answerId: 'skip',
      seconds,
      scoreDelta: 0,
      correctDelta: 0,
    });
  };
  advanceSlotSkipRef.current = advanceSlotSkip;

  const handleSkipQuestion = () => {
    if (selectedAnswer !== null || mcValidating || showFeedback) return;
    const elapsed = Math.min(
      mcLimit,
      Math.max(0, Math.floor((Date.now() - questionShownAtRef.current) / 1000))
    );
    advanceSlotSkip(elapsed);
  };

  const handleQuitQuiz = () => setShowQuitModal(true);

  const confirmQuit = () => {
    stopTimer();
    setShowQuitModal(false);
    onAbandonSession();
  };

  // Only when the question changes — do not list resetTimer: useQuizTimer returns a new function every render,
  // which would re-run this effect every tick and keep resetting the countdown (timer stuck at full).
  useEffect(() => {
    questionShownAtRef.current = Date.now();
    resetTimer();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: stable reset on question id only
  }, [question.id]);

  useBlurMobileSkipFocusOnQuestionChange(slotIndex);

  const handleNextRef = useRef<() => void>(() => {});

  const applyAdvance = () => {
    if (!selectedAnswer || !mcValidateResult) return;
    const elapsed = Math.min(
      mcLimit,
      Math.max(0, Math.floor((Date.now() - questionShownAtRef.current) / 1000))
    );
    secondsRef.current = elapsed;
    const pts = mcValidateResult.correct ? maxPointsForPlayDifficultyBand(band) : 0;
    stopTimer();
    onComplete({
      answerId: selectedAnswer,
      seconds: elapsed,
      scoreDelta: pts,
      correctDelta: mcValidateResult.correct ? 1 : 0,
    });
  };

  handleNextRef.current = applyAdvance;

  useEffect(() => {
    if (!showFeedback || !selectedAnswer || !mcValidateResult) return;
    const id = window.setTimeout(() => handleNextRef.current(), WITC_REVEAL_DELAY_MS);
    return () => window.clearTimeout(id);
  }, [showFeedback, selectedAnswer, mcValidateResult]);

  const [mcImgExt, setMcImgExt] = useState<'webp' | 'png'>('webp');
  const mcUrls = useMemo(() => {
    const base = question.soloQuestionImageBasePath?.trim() || quiz.mcImageAssetsBasePath || '';
    if (!question.imageAssetKey) return null;
    return quizQuestionImageUrlsFromKey(base, question.imageAssetKey);
  }, [quiz.mcImageAssetsBasePath, question.imageAssetKey, question.soloQuestionImageBasePath]);

  const mcAnswerImageBase = useMemo(() => {
    const useImages = question.soloMcUseImageAnswers === true || quiz.mcUseImageAnswers === true;
    if (!useImages) return null;
    const p =
      question.soloMcAnswerImageBasePath?.trim() || quiz.mcAnswerImageAssetsBasePath?.trim() || '';
    return p || null;
  }, [
    question.soloMcUseImageAnswers,
    question.soloMcAnswerImageBasePath,
    quiz.mcUseImageAnswers,
    quiz.mcAnswerImageAssetsBasePath,
  ]);

  const mcUsesImageAnswers = useMemo(() => {
    if (!mcAnswerImageBase) return false;
    return question.answers.some((a) => Boolean(a.imageAssetKey?.trim()));
  }, [mcAnswerImageBase, question.answers]);

  const panelBg = useMemo(
    () => quizQuestionPanelBackground(slotIndex, question.id),
    [slotIndex, question.id]
  );

  const handlePick = async (answerId: string) => {
    if (selectedAnswer !== null || mcValidating || !wasmReady) return;
    setMcValidating(true);
    try {
      const wasmRes = await wasmSubmitAnswer(question.id, answerId);
      const correct = wasmRes?.isCorrect ?? false;
      if (correct) playQuizSfxCorrect();
      else playQuizSfxIncorrect();
      const elapsed = Math.min(
        mcLimit,
        Math.max(0, Math.floor((Date.now() - questionShownAtRef.current) / 1000))
      );
      secondsRef.current = elapsed;
      setMcValidateResult({
        correct,
        correctAnswerId: wasmRes?.correctAnswerId ?? undefined,
      });
      setSelectedAnswer(answerId);
      setShowFeedback(true);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error(msg);
    } finally {
      setMcValidating(false);
    }
  };

  if (slotLoadError || wasmError) {
    return (
      <div className="quiz-page d-flex flex-column align-items-center justify-content-center min-vh-50 text-white">
        <Alert variant="danger" className="mx-3 text-center">
          {slotLoadError || wasmError}
        </Alert>
        <Button variant="outline-light" className="mt-3" onClick={onAbandonSession}>
          {t('quiz.backToHome', 'Back to Home')}
        </Button>
      </div>
    );
  }

  if (!wasmReady) {
    return (
      <div className="quiz-finish-saving-overlay" role="status" aria-live="polite" aria-busy="true">
        <div className="quiz-finish-saving-overlay__inner">
          <Spinner animation="border" role="presentation" />
          <span className="text-white mt-2 small">{t('quiz.loadingQuiz', 'Loading game...')}</span>
        </div>
      </div>
    );
  }

  const revealWrong = showFeedback && mcValidateResult && !mcValidateResult.correct;

  return (
    <div className="quiz-page">
      <div className="quiz-container">
        <div className="quiz-header quiz-page-header">
          <QuizPlayPageHeading quiz={quiz} language={i18n.language} />
          <div className="quiz-page-header__meta d-flex align-items-center justify-content-between gap-2">
            <div className="d-flex gap-2 align-items-center flex-wrap min-w-0 flex-grow-1">
              <Badge
                bg={
                  bandLabel === 'Easy'
                    ? 'success'
                    : bandLabel === 'Medium'
                      ? 'warning'
                      : 'danger'
                }
              >
                {t(`quiz.${bandLabel.toLowerCase()}`, bandLabel)}
              </Badge>
              <span className="text-muted quiz-page-header__question-of">
                {t('quiz.questionOf', 'Question {{current}} of {{total}}')
                  .replace('{{current}}', String(slotIndex + 1))
                  .replace('{{total}}', String(totalQuestions))}
              </span>
              <Badge bg={timeLeft <= 10 ? 'danger' : timeLeft <= 20 ? 'warning' : 'info'} className="timer-badge">
                {t('quiz.timeSeconds', '{{time}}s').replace('{{time}}', String(timeLeft))}
              </Badge>
            </div>
            <div className="d-flex align-items-center gap-2 flex-shrink-0">
              <NavbarMusicToggle className="flex-shrink-0" />
              <QuizSoundToggleButton disabled={mcValidating || showFeedback} />
              <Button
                type="button"
                variant="outline-danger"
                onClick={handleQuitQuiz}
                className="quiz-quit-icon-btn flex-shrink-0"
                aria-label={t('quiz.quitQuiz', 'Quit Quiz')}
                title={t('quiz.quitQuiz', 'Quit Quiz')}
              >
                <i className="bi bi-x-lg" aria-hidden />
              </Button>
            </div>
          </div>
        </div>

        <ProgressBar
          now={((slotIndex + 1) / totalQuestions) * 100}
          className="quiz-progress-bar"
          label={`${Math.round(((slotIndex + 1) / totalQuestions) * 100)}%`}
        />

        <Card className="question-card quiz-compact">
          <Card.Body>
            <Card.Title as="h3" className={`question-title ${mcUrls ? 'question-title--above-image' : ''}`}>
              {pickBilingualLine(question.text, question.textEn, i18n.language)}
            </Card.Title>
            {mcUrls ? (
              <div
                className="mc-question-image-panel mb-3 rounded overflow-hidden position-relative d-flex align-items-center justify-content-center"
                style={{ maxHeight: 320, minHeight: 200, backgroundColor: panelBg }}
              >
                <Button
                  type="button"
                  variant="light"
                  size="sm"
                  onClick={onQuizSkipClick(handleSkipQuestion)}
                  disabled={selectedAnswer !== null || mcValidating || showFeedback}
                  className="witc-skip-on-image-btn d-md-none"
                  aria-label={t('quiz.skipQuestionBtn', 'Skip Question')}
                  title={t('quiz.skipQuestionBtn', 'Skip Question')}
                >
                  <i className="bi bi-skip-forward" aria-hidden />
                </Button>
                <img
                  src={mcImgExt === 'webp' ? mcUrls.webp : mcUrls.png}
                  alt=""
                  className="mc-question-image witc-image-silhouette d-block"
                  decoding="async"
                  onError={() => {
                    if (mcImgExt === 'webp') setMcImgExt('png');
                  }}
                />
              </div>
            ) : null}

            <div className={`answers-container${mcUrls ? ' answers-container--after-question-image' : ''}${mcUsesImageAnswers ? ' answers-container--image-options' : ''}`}>
              {question.answers.map((answer) => {
                const isRevealedCorrect =
                  revealWrong && mcValidateResult?.correctAnswerId === answer.id;
                const pickedWrong =
                  revealWrong && selectedAnswer === answer.id && !mcValidateResult?.correct;
                const variant = isRevealedCorrect
                  ? 'success'
                  : selectedAnswer === answer.id
                    ? pickedWrong
                      ? 'danger'
                      : 'primary'
                    : 'outline-primary';
                const answerLabel = pickBilingualLine(answer.text, answer.textEn, i18n.language);
                const answerImageKey = answer.imageAssetKey?.trim();
                if (mcAnswerImageBase && answerImageKey) {
                  return (
                    <McImageAnswerButton
                      key={answer.id}
                      basePath={mcAnswerImageBase}
                      imageAssetKey={answerImageKey}
                      variant={variant}
                      className={selectedAnswer === answer.id ? 'selected' : ''}
                      onClick={() => void handlePick(answer.id)}
                      disabled={selectedAnswer !== null || mcValidating}
                      showCorrectIcon={Boolean(isRevealedCorrect)}
                      fallbackLabel={answerLabel}
                    />
                  );
                }
                return (
                  <Button
                    key={answer.id}
                    variant={variant}
                    className={`answer-button ${selectedAnswer === answer.id ? 'selected' : ''}`}
                    onClick={() => void handlePick(answer.id)}
                    disabled={selectedAnswer !== null || mcValidating}
                  >
                    {isRevealedCorrect ? <i className="bi bi-check-circle-fill me-2" aria-hidden /> : null}
                    {answerLabel}
                  </Button>
                );
              })}
            </div>

            {showFeedback && selectedAnswer && mcValidateResult && (
              <div className="answer-feedback">
                <Alert variant={mcValidateResult.correct ? 'success' : 'danger'}>
                  <i className={`bi ${mcValidateResult.correct ? 'bi-check-circle' : 'bi-x-circle'}`} />
                  {mcValidateResult.correct
                    ? ` ${t('quiz.correctAnswer', 'Correct answer!')}`
                    : ` ${t('quiz.incorrectAnswer', 'Incorrect answer.')}`}
                </Alert>
              </div>
            )}

            <div className="quiz-stats-actions-container">
              <div className="quiz-stats-inline">
                <div className="stat-line">
                  {t('quiz.score', 'Score')}: {runningScore}
                </div>
                <div className="stat-line">
                  {t('quiz.correctAnswers', 'Correct')}: {runningCorrect}/{totalQuestions}
                </div>
              </div>
              <div className="quiz-actions quiz-footer-actions">
                <Button
                  variant="outline-secondary"
                  onClick={onQuizSkipClick(handleSkipQuestion)}
                  className={`quiz-action-btn align-items-center${mcUrls ? ' d-none d-md-inline-flex' : ''}`}
                  disabled={selectedAnswer !== null || mcValidating || showFeedback}
                >
                  <i className="bi bi-skip-forward me-1 me-sm-2" />
                  {t('quiz.skipQuestionBtn', 'Skip Question')}
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>

      <Modal show={showQuitModal} onHide={() => setShowQuitModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t('quiz.quitQuizModal', 'Quit Quiz?')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t(
            'quiz.quitQuizConfirm',
            'Are you sure you want to quit this quiz? Your progress will be lost and no points will be awarded.'
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowQuitModal(false)}>
            {t('quiz.continueQuiz', 'Continue Quiz')}
          </Button>
          <Button variant="danger" onClick={confirmQuit}>
            {t('quiz.quitQuiz', 'Quit Quiz')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default QuickMatchMixedPlayer;
